import { z } from 'zod';
import { addressSchema, sortablePageParamsSchema, type PagedResult } from '#api/common.ts';
import { metaSchema, recordSchema } from '#api/common.ts';
import { DeliverabilityStatus } from '#lib/models/enums.ts';
import { GenericApi } from './genericApi';

export const AccountNumberFormat = z.enum(['SequentialNumber']);
export type AccountNumberFormat = z.infer<typeof AccountNumberFormat>;

export const PrefixType = z.enum(['None', 'ProductCode', 'Constant']);
export type PrefixType = z.infer<typeof PrefixType>;

export const portalSettingsSchema = z.object({
    portalUrl: z.union([z.literal(''), z.url({ protocol: /^https$/, hostname: z.regexes.domain })]).default(''),
    logoUrl: z.string().default(''),
    favIconUrl: z.string().default(''),
    themeData: z.record(z.string(), z.any()).default({}).nullable(),
    isRegistrationEnabled: z.boolean().default(false),
    isLoginEnabled: z.boolean().default(false),
    isExternalPortal: z.boolean().default(false),
    privacyPolicyHtml: z.string().nullable().default(null),
    termsHtml: z.string().nullable().default(null)
});

export const disclosuresSchema = z.object({
    autopayAgentTemplate: z.string().default(''),
    autopayObligorTemplate: z.string().default('')
});

export const CollectionsType = z.enum(['DoNotCollect', 'Servicer', 'SelfService']);
export type CollectionsType = z.infer<typeof CollectionsType>;

export { ServicingMode } from '#lib/models/enums.ts';
import { ServicingMode } from '#lib/models/enums.ts';

const collectionsTypeLabels: Record<CollectionsType, string> = {
    DoNotCollect: 'Do Not Collect',
    Servicer: 'Servicer',
    SelfService: 'Self Service'
};

export const collectionsTypeLabel = (type: CollectionsType | string) =>
    collectionsTypeLabels[type as CollectionsType] ?? type;

export const contactDeliverabilityResponseSchema = z.object({
    appliedStatus: DeliverabilityStatus.default('Undeliverable'),
    excludeFromComms: z.boolean().default(true)
});

export const contactDeliverabilitySettingsSchema = z.object({
    address: contactDeliverabilityResponseSchema
});

export const collectionsSettingsSchema = z.object({
    collectionsType: CollectionsType.default('DoNotCollect'),
    daysDelinquent: z.number().int().min(0).default(0),
    maxDaysDelinquent: z.number().int().min(1).nullish(),
    defaultFollowUpDays: z.number().int().min(0).max(15).nullish()
});

export const accountNumberGenerationStrategySchema = z
    .object({
        totalLength: z.number().default(0),
        prefixConstant: z.string().optional(),
        prefixType: PrefixType.default('None'),
        accountNumberFormat: AccountNumberFormat.default('SequentialNumber')
    })
    .superRefine((strategy, ctx) => {
        if (
            strategy.prefixType == PrefixType.enum.Constant &&
            (strategy.prefixConstant === undefined || strategy.prefixConstant == null || strategy.prefixConstant == '')
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'You must supply a Prefix Constant for the Constant prefix type.',
                path: ['prefixConstant']
            });
        }
    });

export const clientSchema = z.object({
    id: z.number(),
    name: z.string(),
    code: z.string(),
    timeZone: z.string(),
    website: z.string().optional(),
    phone: z.string().optional(),
    updatedUtc: z.coerce.date().optional(),
    createdUtc: z.coerce.date().optional(),
    address: addressSchema,
    allowedOutboundAddresses: z.array(z.string()).optional().default([]),
    externalFlexOrigins: z.array(z.string()).optional().default([]),
    portalSettings: portalSettingsSchema.optional(),
    disclosures: disclosuresSchema.optional(),
    collections: collectionsSettingsSchema.optional(),
    contactDeliverability: contactDeliverabilitySettingsSchema.optional(),
    accountNumberGenerationStrategy: accountNumberGenerationStrategySchema.optional(),
    servicingMode: ServicingMode.optional(),
    data: z.union([recordSchema, z.null()]).optional(),
    meta: metaSchema.optional()
});

export const clientSummarySchema = z.object({
    id: z.number(),
    name: z.string(),
    code: z.string(),
    timeZone: z.string(),
    servicingMode: ServicingMode.optional(),
    portalSettings: z.object({ favIconUrl: z.string().default('') }).nullish()
});

export const listClientSchema = sortablePageParamsSchema.extend({
    obligationId: z.number().optional(),
    search: z.string().optional(),
    includeCounts: z.boolean().optional(),
    includeDataFields: z.boolean().optional(),
    summary: z.boolean().optional()
});

export const createClientSchema = z.object({
    name: z.string().min(2, { message: 'Name must be at least 2 characters' }),
    code: z.string().length(4, { message: 'Code must be 4 characters' }),
    timeZone: z.string().min(1, { message: 'Time Zone is required' }),
    website: z.string().url({ message: 'Website must be a valid URL' }),
    phone: z.string().min(7, { message: 'Phone must be at least 7 characters' }),
    address: addressSchema.optional(),
    allowedOutboundAddresses: z.array(z.string()).optional(),
    externalFlexOrigins: z.array(z.string()).optional(),
    portalSettings: portalSettingsSchema.optional(),
    accountNumberGenerationStrategy: accountNumberGenerationStrategySchema.optional(),
    disclosures: disclosuresSchema.optional(),
    collections: collectionsSettingsSchema.optional(),
    contactDeliverability: contactDeliverabilitySettingsSchema.optional(),
    data: z.union([recordSchema, z.null()]).optional()
});

export const updateClientSchema = createClientSchema.partial().extend({
    servicingMode: ServicingMode.optional()
});

export type Client = z.infer<typeof clientSchema>;
export type ClientSummary = z.infer<typeof clientSummarySchema>;
export type CreateClient = z.infer<typeof createClientSchema>;
export type UpdateClient = z.infer<typeof updateClientSchema>;
export type ListClient = z.infer<typeof listClientSchema>;
export type PortalSettings = z.infer<typeof portalSettingsSchema>;
export type Disclosures = z.infer<typeof disclosuresSchema>;
export type CollectionsSettings = z.infer<typeof collectionsSettingsSchema>;
export type ContactDeliverabilitySettings = z.infer<typeof contactDeliverabilitySettingsSchema>;
export type ContactDeliverabilityResponse = z.infer<typeof contactDeliverabilityResponseSchema>;
export type AccountNumberGenerationStrategy = z.infer<typeof accountNumberGenerationStrategySchema>;

export class ClientApi extends GenericApi<Client, CreateClient, UpdateClient, ListClient> {
    constructor() {
        super('clients');
    }

    async list(params: ListClient = {}, signal?: AbortSignal) {
        return await this.fetch.getJSON<PagedResult<Client>>(this.route, { params, signal });
    }
}
